import { ToggleChip } from '@eventbrite/marmalade';
import React, { forwardRef, useRef, useState } from 'react';
import { LocationData, PageType } from '../../types/AppProps';
import { DateFilterDropdown, DateOption } from '../DateFilterDropdown';
import { Carousel } from './Carousel';
import styles from './categoryFilterBar.module.scss';
import { ICategoryToIconMap } from './categoryToIconMap';
import { useScrollToActiveChip, useToggleStickyCarousel } from './hooks';

export interface CategoryProps {
    categories: Array<ICategoryToIconMap>;
    pageType: PageType;
    locationData: LocationData;
    enableQuery: Function;
    dateSelection: DateOption;
    changeDateSelection: React.Dispatch<React.SetStateAction<DateOption>>;
    setCategoryTag: Function;
    handleToggled: Function;
    selectedCategory: ICategoryToIconMap;
    enableDateFilter: boolean | null;
}

// Figma Reference: https://www.figma.com/file/uMg0ZCn5FmtVv1vxNR6AfX/Things-To-Do?type=design&node-id=1435-62902&mode=dev
export const CategoryFilterBar = forwardRef<HTMLDivElement, CategoryProps>(
    (
        {
            categories,
            pageType,
            enableQuery,
            dateSelection,
            changeDateSelection,
            setCategoryTag,
            handleToggled,
            selectedCategory,
            enableDateFilter,
        },
        ref,
    ) => {
        const refs: Array<React.RefObject<HTMLButtonElement> | undefined> =
            categories.map(() => React.createRef());
        const scrollBarContainerRef = useRef<HTMLDivElement>(null);

        const [initialScrollPosition, setInitialScrollPosition] = useState(0);
        useToggleStickyCarousel(scrollBarContainerRef);
        useScrollToActiveChip(
            refs,
            categories,
            selectedCategory,
            setInitialScrollPosition,
        );

        const handleClick = (idx: number) => {
            enableQuery();
            handleToggled(idx);
            setCategoryTag(categories[idx].category);
        };

        return (
            <div id="scrollContainerId" ref={scrollBarContainerRef}>
                <div
                    className={`${styles.categoryFilterBar} ${pageType}__carousel`}
                >
                    {enableDateFilter ? (
                        <DateFilterDropdown
                            dateSelection={dateSelection}
                            changeDateSelection={changeDateSelection}
                            enableQuery={enableQuery}
                            pageType={pageType}
                        />
                    ) : null}
                    <Carousel scrollPosition={initialScrollPosition} ref={ref}>
                        {categories.map((category, idx) => {
                            const selected = selectedCategory === category;
                            const toggledClassname = selected ? 'toggled' : '';
                            return (
                                <ToggleChip
                                    key={category.category.toString()}
                                    icon={category.icon}
                                    aria-label={category.category.toString()}
                                    data-heap-id={`${pageType}-${category.heapSelector}`}
                                    className={`${styles.toggleChip} ${toggledClassname}`}
                                    iconTreatment="alwaysShow"
                                    onClick={() => handleClick(idx)}
                                    ref={refs[idx]}
                                    /*
                                    The toggle chip component does not currently have support for a toggle chip group
                                    and does not allow us to defined the pressed/not pressed state of the chip.
                                    Because of that, we have to store the toggled state ourselves, and update the
                                    CSS selectors manually
                                    */
                                    aria-pressed={selected}
                                    data-state={selected ? 'on' : 'off'}
                                >
                                    {category.category}
                                </ToggleChip>
                            );
                        })}
                    </Carousel>
                </div>
            </div>
        );
    },
);
